import React, { useState } from 'react';
import { useOTPAuth } from '../providers/OTPAuthProvider';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { ArrowBack, Send } from '@mui/icons-material';

interface EmailLoginFormProps {
  onSuccess: (email: string) => void;
  onBack?: () => void;
  onRedirectToMicrosoft?: () => void;
}

const EmailLoginForm: React.FC<EmailLoginFormProps> = ({ onSuccess, onBack, onRedirectToMicrosoft }) => {
  const { requestOTP } = useOTPAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validate email
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check if email is from @horwathhtl.com domain
    if (email.toLowerCase().endsWith('@horwathhtl.com')) {
      setSuccessMessage('Redirecting to Horwath HTL sign in...');
      // Wait a moment to show the message
      setTimeout(() => {
        if (onRedirectToMicrosoft) {
          onRedirectToMicrosoft();
        }
      }, 1000);
      return;
    }

    try {
      setLoading(true);
      const result = await requestOTP(email.toLowerCase());
      
      if (result.success) {
        setSuccessMessage(result.message);
        // Wait a moment to show the success message
        setTimeout(() => {
          onSuccess(email.toLowerCase());
        }, 1500);
      } else {
        setError(result.message);
      }
    } catch (_err) {
      void _err;
      setError('Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      {onBack && (
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            Back
          </Button>
        </Box>
      )}
      
      <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
        Sign in with Email
      </Typography>
      <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
        We'll send you a verification code to sign in
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          type="email"
          label="Email Address"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          autoFocus
          autoComplete="email"
          sx={{ mb: 3 }}
          InputProps={{
            sx: {
              bgcolor: 'background.paper',
            }
          }}
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          size="large"
          disabled={loading || !email}
          endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
          sx={{
            bgcolor: '#16a34a',
            '&:hover': { bgcolor: '#15803d' },
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          {loading ? 'Sending Code...' : 'Send Verification Code'}
        </Button>
      </form>

      <Typography 
        variant="caption" 
        align="center" 
        display="block" 
        sx={{ mt: 3, color: 'text.secondary' }}
      >
        Having trouble? Contact{' '}
        <Link href="mailto:support@horwathhtl.com" color="primary">
          support@horwathhtl.com
        </Link>
      </Typography>
    </Box>
  );
};

export default EmailLoginForm;